import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale } from 'lucide-react';

const H2 = ({ children }) => (
  <h2 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-3">{children}</h2>
);
const Section = ({ children }) => (
  <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">{children}</section>
);
const P = ({ children }) => <p className="text-[#374151] leading-relaxed mb-3 last:mb-0">{children}</p>;

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="terms-page">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>

      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/lifelink-logo.png" alt="LIFE Link" className="h-10 w-auto object-contain" />
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#34657f] hover:underline rounded px-2 py-1.5"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to sign in
          </Link>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-3xl mx-auto px-6 py-10 outline-none">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#34657f] text-white">
            <Scale className="w-6 h-6" aria-hidden="true" />
          </span>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
            Terms &amp; Conditions
          </h1>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-8">Effective date: June 2026</p>

        <Section>
          <H2>1. Acceptance of these Terms</H2>
          <P>
            These Terms &amp; Conditions ("Terms") govern your use of LIFE Link (the "Portal"), operated by
            Butler Tech's Project LIFE program in partnership with Aspiritech. By signing in to or using the
            Portal, you agree to these Terms and to our{' '}
            <Link to="/privacy" className="text-[#1f7fa6] underline font-medium">Privacy Policy</Link>. If you
            do not agree, do not use the Portal.
          </P>
        </Section>

        <Section>
          <H2>2. Eligibility and accounts</H2>
          <ul className="list-disc pl-5 space-y-2 text-[#374151] leading-relaxed mb-3 last:mb-0">
            <li>The Portal is intended solely for authorized adult professionals affiliated with Project LIFE (administrators, instructors, skills trainers, and approved evaluators). It is not intended for use by students or children.</li>
            <li>Accounts are provisioned by program administrators. You are responsible for keeping your credentials confidential and for all activity under your account.</li>
            <li>Notify us promptly at <a href="mailto:projectlife@butlertech.org" className="text-[#34657f] font-bold underline">projectlife@butlertech.org</a> if you suspect unauthorized use of your account.</li>
            <li>Demo accounts are time-limited, view-only, and may be revoked at any time.</li>
          </ul>
        </Section>

        <Section>
          <H2>3. Acceptable use</H2>
          <P>You agree NOT to:</P>
          <ul className="list-disc pl-5 space-y-2 text-[#374151] leading-relaxed mb-3 last:mb-0">
            <li>Post content that is unlawful, defamatory, harassing, discriminatory, or otherwise inappropriate for a professional education community;</li>
            <li>Share student personally identifiable information, photos, or records without all consents required by law and your district's policies;</li>
            <li>Upload material that infringes any copyright, trademark, or other proprietary right;</li>
            <li>Share your account, or attempt to access accounts, data, or areas of the Portal you are not authorized to access;</li>
            <li>Introduce malware, scrape the Portal, or interfere with its security or operation;</li>
            <li>Redistribute, sell, or publicly post Portal materials outside your authorized program use (see Section 5).</li>
          </ul>
        </Section>

        <Section>
          <H2>4. User content</H2>
          <ul className="list-disc pl-5 space-y-2 text-[#374151] leading-relaxed mb-3 last:mb-0">
            <li>You retain ownership of content you post (forum posts, comments, stories, photos, files).</li>
            <li>By posting, you grant Butler Tech and Aspiritech a non-exclusive, royalty-free license to host, display, and share that content within the Portal for program purposes.</li>
            <li>You represent that you have all rights and consents necessary to post your content — including written parental/guardian consent for any student photos or information, as required by FERPA and district policy.</li>
            <li>We may remove any content or suspend any account at our discretion, including content reported as inappropriate or non-compliant.</li>
          </ul>
        </Section>

        <Section>
          <H2>5. Intellectual property</H2>
          <P>
            The Project LIFE curriculum, toolkits, training materials, logos, and all other Portal content
            (other than user content) are the property of Butler Tech and/or its licensors and are protected
            by copyright and other laws. You are granted a limited, non-transferable license to access,
            download, and copy these materials solely for use within your authorized Project LIFE program.
            Any other reproduction, distribution, public posting, or commercial use requires prior written
            permission from Butler Tech.
          </P>
        </Section>

        <Section>
          <H2>6. Third-party services</H2>
          <P>
            The Portal integrates optional third-party services, such as Google Drive (to copy resources to
            your own Drive) and links to external websites. Your use of those services is governed by their
            own terms and privacy policies, and we are not responsible for third-party services or content.
          </P>
        </Section>

        <Section>
          <H2>7. Termination</H2>
          <P>
            We may suspend or terminate your access at any time, with or without notice, including for
            violation of these Terms, at the direction of your program, or upon the end of your affiliation
            with Project LIFE. Sections 4–11 survive termination.
          </P>
        </Section>

        <Section>
          <H2>8. Disclaimer of warranties</H2>
          <P>
            THE PORTAL AND ALL MATERIALS ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY
            KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED AVAILABILITY. EDUCATIONAL MATERIALS ARE
            PROVIDED FOR PROGRAM GUIDANCE AND DO NOT CONSTITUTE LEGAL, MEDICAL, OR PROFESSIONAL ADVICE FOR
            ANY INDIVIDUAL STUDENT.
          </P>
        </Section>

        <Section>
          <H2>9. Limitation of liability</H2>
          <P>
            TO THE FULLEST EXTENT PERMITTED BY LAW, BUTLER TECH, ASPIRITECH, AND THEIR RESPECTIVE OFFICERS,
            EMPLOYEES, PARTNERS, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
            CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES — INCLUDING LOSS OF DATA, PROFITS, OR GOODWILL —
            ARISING FROM OR RELATING TO YOUR USE OF (OR INABILITY TO USE) THE PORTAL, EVEN IF ADVISED OF THE
            POSSIBILITY OF SUCH DAMAGES. IN NO EVENT SHALL AGGREGATE LIABILITY ARISING OUT OF THE PORTAL
            EXCEED ONE HUNDRED U.S. DOLLARS ($100). NOTHING IN THESE TERMS LIMITS LIABILITY THAT CANNOT BE
            LIMITED UNDER APPLICABLE LAW.
          </P>
        </Section>

        <Section>
          <H2>10. Indemnification</H2>
          <P>
            You agree to indemnify and hold harmless Butler Tech, Aspiritech, and their respective officers,
            employees, partners, and agents from any claims, damages, losses, and expenses (including
            reasonable attorneys' fees) arising from your content, your violation of these Terms, or your
            violation of any law or third-party right — including student privacy laws.
          </P>
        </Section>

        <Section>
          <H2>11. Governing law</H2>
          <P>
            These Terms are governed by the laws of the State of Ohio, USA, without regard to conflict-of-law
            principles. Any dispute shall be brought exclusively in the state or federal courts located in
            Butler County, Ohio, and you consent to their jurisdiction.
          </P>
        </Section>

        <Section>
          <H2>12. Changes to these Terms</H2>
          <P>
            We may revise these Terms from time to time. Material changes will be noted by updating the
            effective date above. Continued use of the Portal after changes take effect constitutes
            acceptance of the revised Terms.
          </P>
        </Section>

        <Section>
          <H2>13. Contact</H2>
          <P>
            Questions about these Terms? Contact the Project LIFE National Team at{' '}
            <a href="mailto:projectlife@butlertech.org" className="text-[#34657f] font-bold underline" data-testid="terms-contact-link">
              projectlife@butlertech.org
            </a>.
          </P>
        </Section>

        <p className="text-sm text-[#5C5C5C] text-center pb-6">
          &copy; 2026 LIFE Link | Project LIFE | Butler Tech. All rights reserved. ·{' '}
          <Link to="/privacy" className="text-[#1a6f92] underline">Privacy Policy</Link>
        </p>
      </main>
    </div>
  );
}
